import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  DatasourceEditor,
  ModelDatasourceBuilder,
} from "@/components/portal/apps/reporting/datasources";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import type { DatasourceDefinition } from "@/components/portal/apps/reporting/datasources/types";

export const Route = createFileRoute(
  "/portal/apps/reporting/datasources/$slug_/edit",
)({
  component: EditDatasourcePage,
});

function EditDatasourcePage() {
  const { slug } = Route.useParams();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const { token } = useAuth();

  const [datasource, setDatasource] = useState<DatasourceDefinition | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read the token via a ref so an auth-token rotation does NOT re-fire this
  // effect. Without it, every keep-alive / 401-driven refresh flips `loading`
  // true and unmounts <DatasourceEditor>, wiping the unsaved datasource edit
  // (name / SQL query / columns / measures held in local state). Org or slug
  // change still refetches via the deps below.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!organisationId || !slug) return;

    setLoading(true);
    portalFetch<DatasourceDefinition>(
      `/api/v1/gateway/reporting/api/v1/datasources/${slug}`,
      { token: tokenRef.current ?? undefined },
    )
      .then(setDatasource)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load"),
      )
      .finally(() => setLoading(false));

    // Token read via tokenRef so rotation doesn't remount the editor; only
    // org/slug refetch.
  }, [organisationId, slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !datasource) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error || "Datasource not found"}</p>
      </div>
    );
  }

  if (datasource.sourceType === "model") {
    return <ModelDatasourceBuilder mode="edit" initialData={datasource} />;
  }

  return (
    <DatasourceEditor
      organisationId={organisationId}
      mode="edit"
      initialData={datasource}
    />
  );
}
